import React, { useState, useRef, useEffect } from "react";
import DragAndDropComponent from "../../UILibrary/DragAndDrop";
import Overlay from "../../UILibrary/Overlay";
import MappingTable from "./MappingTable";
import Select from "react-select";
import "../Dashboard.css";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  addSuppliers,
  updateSupplier,
  updateFolderCounts,
  createSupplier,
  editSupplierById,
} from "../../../store/suppliersManagementSlice";

const industries = [
  { code: "11", name: "Agriculture, Forestry, Fishing and Hunting" },
  { code: "21", name: "Mining, Quarrying, and Oil and Gas Extraction" },
  { code: "22", name: "Utilities" },
  { code: "23", name: "Construction" },
  { code: "31-33", name: "Manufacturing" },
  { code: "42", name: "Wholesale Trade" },
  { code: "44-45", name: "Retail Trade" },
  { code: "48-49", name: "Transportation and Warehousing" },
  { code: "51", name: "Information" },
  { code: "52", name: "Finance and Insurance" },
  { code: "53", name: "Real Estate and Rental and Leasing" },
  { code: "54", name: "Professional, Scientific, and Technical Services" },
  { code: "55", name: "Management of Companies and Enterprises" },
  {
    code: "56",
    name: "Administrative and Support and Waste Management and Remediation Services",
  },
  { code: "61", name: "Educational Services" },
  { code: "62", name: "Health Care and Social Assistance" },
  { code: "71", name: "Arts, Entertainment, and Recreation" },
  { code: "72", name: "Accommodation and Food Services" },
  { code: "81", name: "Other Services (except Public Administration)" },
];
const countries = ["Canada", "United States"];

const statesByCountry = {
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Nova Scotia",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
  ],
  "United States": [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ],
};

const stateAbbreviations = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const customStyles = {
  menu: (provided) => ({
    ...provided,
    textAlign: "start",
    background: "#F7F7F7",
    borderRadius: "0.5rem",
  }),
  control: (provided, state) => ({
    ...provided,
    border: "none",
    background: "#F9F9F9",
    textAlign: "start",
    height: "48px",
    borderRadius: "0.5rem",
    boxShadow: state.isFocused
      ? "0 0 0 2px rgba(128, 128, 128, 0.5)"
      : provided.boxShadow,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#7B7B7B",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "lightgray"
      : state.isFocused
      ? "lightgray"
      : provided.backgroundColor,
    color: "black",
    padding: "6px",
    borderRadius: "0.5rem",
    "&:active": {
      backgroundColor: "lightgray",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#B0B0B0",
  }),
};

const sizeOptions = [
  { value: "", label: "Select size", isDisabled: true },
  { value: "1-10", label: "1-10" },
  { value: "11-50", label: "11-50" },
  { value: "50-100", label: "50-100" },
  { value: "100-500", label: "100-500" },
  { value: "500+", label: "500+" },
];

const CreateNewSupplierForm = ({
  setNewSupplierClicked,
  newSupplier,
  editableCompany,
  setEditableCompany,
  isEditableSupplier,
  setEditableSupplier,
  setSupplierCreated,
  setSupplierUpdated,
  setEditPopup,
  setPendingSuppliers,
}) => {
  const [pdfFile, setPdfFile] = useState(null);
  const [importedFiles, setImportedFiles] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [autoFilledData, setAutoFilledData] = useState([]);
  const [newSupplierData, setNewSupplierData] = useState(newSupplier);
  const [showMessage, setShowMessage] = useState(false);
  const [isProceed, setProceed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [zipError, setZipError] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const userId = useSelector((state) => state.auth.id);
  const user = useSelector((state) => state.auth.userDetails.userDetails);
  const [isIndustrySelected, setIndustrySelected] = useState(false);
  const [isNaicsSelected, setNaicsSelected] = useState(false);

  const navigate = useNavigate();
  const folders = useSelector((state) => state.suppliers.folders);
  const folderOptions = folders.map((folder) => ({
    value: folder,
    label: folder.name,
  }));
  const companyName = newSupplierData?.company?.name?.trim();
  const email = newSupplierData?.contact?.type[0]?.email?.trim();
  const [naicsData, setNaicsData] = useState([]);

  const formValid =
    importedFiles.length > 0 ||
    (!!companyName && !!email && (!isIndustrySelected || isNaicsSelected));


  const [fullName, setFullName] = useState(
    `${newSupplierData?.contact?.type[0]?.name?.firstName || ""} ${
      newSupplierData?.contact?.type[0]?.name?.lastName || ""
    }`.trim()
  );
  const dispatch = useDispatch();

  const selectedFolderOption =
    folderOptions.find(
      (opt) =>
        opt?.value?.name?.toLowerCase().trim() ===
        newSupplierData.folder?.name?.toLowerCase().trim()
    ) || null;

  const industryValue = {
    value: newSupplierData.industry.name,
    label: newSupplierData.industry.name,
  };
  const BASE_API_URL =
    "https://api.census.gov/data/2022/ecnbasic?get=NAICS2022,NAICS2022_LABEL,NAME,GEO_ID,ESTAB,RCPTOT,EMP&for=us:*&key=ca9f7d90d4ea4a77f056e0a1ea74f0a8660366b6";
  useEffect(() => {
    if (!newSupplierData.industry?.code) return;

    const apiUrl = `${BASE_API_URL}&NAICS2022=${newSupplierData.industry.code}*`;

    const fetchNaicsData = async () => {
      try {
        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.length > 1) {
          const formattedData = data
            .slice(1)
            .map(([code, description, ...rest]) => ({
              value: code,
              label: `${code} - ${description}`,
              additionalInfo: rest,
            }));
          setNaicsData(formattedData);
        } else {
          setNaicsData([]);
        }
      } catch (error) {
        console.error("Error fetching NAICS data:", error);
        setNaicsData([]);
      }
    };

    fetchNaicsData();
  }, [newSupplierData.industry?.code]);

  const validatePostalCode = (zip, country) => {
    if (country === "USA") {
      const usZipRegex = /^\d{5}$/;
      return usZipRegex.test(zip)
        ? ""
        : "Please enter a valid 5-digit US ZIP code (e.g., 12345).";
    } else if (country === "CAN") {
      const canadaZipRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
      return canadaZipRegex.test(zip)
        ? ""
        : "Please enter a valid Canadian Postal Code (e.g., K1A 0B1).";
    }
    return "";
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };
  const debouncedPostalCode = useDebounce(postalCode, 200);

  useEffect(() => {
    if (debouncedPostalCode) {
      setZipError(validatePostalCode(debouncedPostalCode, selectedCountry));
    } else {
      setZipError("");
    }
  }, [debouncedPostalCode]);

  useEffect(() => {
    if (isProcessing) {
      const newDataArray = autoFilledData.map((data) => ({
        _id: data.id || uuidv4(),
        company: {
          name: data.companyname,
          description: "",
          dba: "",
          website: data.url,
          businessSize: {
            class: "",
            class_type: "",
            min: "",
            max: "",
            currency: "",
            value: "",
            operator: "",
          },
          employeeRange: {
            min: "",
            max: "",
            value: "",
            operator: "",
          },
        },
        identifiers: {
          identifierDetails: [
            {
              name: "",
              description: "EIN",
              code: "",
              type: "EIN",
            },
            {
              name: "",
              description: "DUNS number",
              code: "",
              type: "DUNS",
            },
          ],
        },
        socialMedia: [],
        contact: {
          type: [
            {
              identifier: "primary",
              Salutation: "",
              name: {
                firstName: "",
                lastName: "",
                middleName: "",
              },
              gender: "",
              ethnicity: "",
              title: "",
              email: "",
              phoneAreaCode: "",
              phoneNumber: "",
              phoneExtn: "",
              faxAreaCode: "",
              faxNumber: "",
              tollFreePhoneAreaCode: "",
              tollFreePhoneNumber: "",
              tollFreePhoneExtn: "",
            },
          ],
        },
        address: {
          type: [
            {
              identifier: "physical",
              country: "",
              state: "",
              city: "",
              street: "",
              misc: "",
              address_line_01: "",
              address_line_02: "",
              address_line_03: "",
              postalCode: "",
              division: "",
              zip_01: "",
              zip_02: "",
              Latitude: "",
              Longitude: "",
            },
          ],
        },
        diversity: {
          certificationType: [
            {
              classification: data.ownership,
              ethnicity_status: "",
              type: data.certification,
              subType: "",
              renewalDate: "",
              certifiedSince: "",
              certificationNumber: "",
              status: "",
              summary: "",
            },
          ],
          agency: {
            details: [
              {
                name: "",
                isCertified: "",
                description: "",
              },
            ],
          },
        },
        capability: {
          capabilityDetails: [],
        },
        industry: {
          name: data.companytype,
          description: "",
          tag: "",
        },
        services: {
          servicesDetails: [],
        },
        commodityCode: {
          ccDetails: [],
        },
        goods: {
          goodDetails: [],
        },
        folder: {
          name: newSupplierData.folder.name,
          numberOfSuppliers: "",
        },
      }));

      dispatch(
        updateFolderCounts({
          folderName: newSupplierData.folder.name,
          count: newDataArray.length,
        })
      );
      dispatch(addSuppliers(newDataArray));

      setSupplierCreated(true);
      setShouldNavigate(true);
      setIsProcessing(false);
      navigate("/dashboard/management/create-new-supplier/mapping-table", {
        state: {
          suppliers: autoFilledData,
          importedFiles: importedFiles[0],
          folder: selectedFolderOption,
        },
      });
    }
  }, [isProcessing]);

  const handleCreateByFile = (event) => {
    if (event) event.preventDefault();
    setIsProcessing(true);
  };

  const handleChange = (field, value) => {
    setNewSupplierData((prevState) => ({
      ...prevState,
      company: {
        ...prevState.company,
        [field]: value,
      },
    }));
    if (field === "zipCode") {
      setPostalCode(value);
    }
  };

  const selectedOption =
    sizeOptions.find((opt) => opt.value === newSupplierData.size) ||
    sizeOptions[2];

  useEffect(() => {
    if (editableCompany) {
      setEditableSupplier(true);
      setNewSupplierData(editableCompany);
    }
  }, [editableCompany]);

  const handleEditConfirmation = () => {
    const buyerIdToUse = user?.isTestUser ? user?.id : userId;
    setNewSupplierClicked(false);
    setSupplierUpdated(true);
    setEditPopup(true);
    dispatch(updateSupplier(newSupplierData));
    const updatedData = {
      website: newSupplierData?.company?.website,
      companyName: newSupplierData?.company?.name,
      companyEmail: newSupplierData?.contact?.type[0]?.email,
      industry: newSupplierData?.industry?.name,
      companySize: `${newSupplierData.company.businessSize?.min || ""}-${
        newSupplierData.company.businessSize?.max || ""
      }`,
      country: newSupplierData?.address?.type[0]?.country,
      state: newSupplierData?.address?.type[0]?.state,
      city: newSupplierData?.address?.type[0]?.city,
      street: newSupplierData?.address?.type[0]?.street,
      zipCode: Number(newSupplierData?.address?.type[0]?.postalCode),
      primaryContact: `${
        newSupplierData.contact.type?.[0]?.name?.firstName || ""
      } ${newSupplierData.contact.type?.[0]?.name?.lastName || ""}`,
      dunsNumber: extractIdentifier(
        newSupplierData?.identifiers?.identifierDetails,
        "DUNS"
      ),
      ein: extractIdentifier(
        newSupplierData?.identifiers?.identifierDetails,
        "EIN"
      ),
      niaicsCodes: extractIdentifier(
        newSupplierData?.identifiers?.identifierDetails,
        "NAICS"
      ),
      addToFolder: newSupplierData.folder?.name,
      createdBy: buyerIdToUse,
    };
    dispatch(
      editSupplierById({
        supplierId: newSupplierData._id,
        updatedData,
      })
    );
  };

  const handleCancel = () => {
    setNewSupplierClicked(false);
    setEditableSupplier(false);
    setEditableCompany(null);
  };

  const handleFileChange = (files) => {
    console.log("files", files);
  };

  const handleNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleDelete = (fileName) => {
    setImportedFiles((prevImportedFiles) =>
      prevImportedFiles.filter((file) => file.name !== fileName)
    );
  };

  const countryOptions = countries.map((country) => ({
    value: country,
    label: country,
  }));

  const selectedCountry = newSupplierData.address?.type[0]?.country || "USA";

  const stateOptions =
    selectedCountry && statesByCountry[selectedCountry]
      ? [
          { value: "", label: "Select", isDisabled: true },
          ...statesByCountry[selectedCountry].map((state) => ({
            value: state,
            label: state,
          })),
        ]
      : [{ value: "", label: "Select", isDisabled: true }];
  const extractIdentifier = (identifierDetails, type) => {
    return (
      identifierDetails.find((id) => id.type === type || id.name === type)
        ?.code || ""
    );
  };

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    const buyerIdToUse = user?.isTestUser ? user?.id : userId;

    if (currentStep === 3) {
      dispatch(
        createSupplier({
          website: newSupplierData?.company?.website,
          companyName: newSupplierData?.company?.name,
          companyEmail: newSupplierData?.contact?.type[0]?.email,
          industry: newSupplierData?.industry?.name,
          companySize: `${newSupplierData.company.businessSize?.min || ""}-${
            newSupplierData.company.businessSize?.max || ""
          }`,
          country: newSupplierData?.address?.type[0]?.country,
          state: newSupplierData?.address?.type[0]?.state,
          city: newSupplierData?.address?.type[0]?.city,
          street: newSupplierData?.address?.type[0]?.street,
          zipCode: Number(newSupplierData?.address?.type[0]?.postalCode),
          primaryContact: `${
            newSupplierData.contact.type?.[0]?.name?.firstName || ""
          } ${newSupplierData.contact.type?.[0]?.name?.lastName || ""}`,
          dunsNumber: extractIdentifier(
            newSupplierData?.identifiers?.identifierDetails,
            "DUNS"
          ),
          ein: extractIdentifier(
            newSupplierData?.identifiers?.identifierDetails,
            "EIN"
          ),
          niaicsCodes: extractIdentifier(
            newSupplierData?.identifiers?.identifierDetails,
            "NAICS"
          ),
          addToFolder: newSupplierData?.folder?.name,
          createdBy: buyerIdToUse,
        })
      );

      setSupplierCreated(true);
      setNewSupplierClicked(false);

      dispatch(
        updateFolderCounts({
          folderName: newSupplierData.folder.name,
          count: 1,
        })
      );
    }

    handleNextStep();
  };
  return (
    <div>
      <Overlay />
      <div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1100]">
        <div className="relative flex flex-col items-center justify-start px-[51px] pt-0 w-[658px] h-[830px] rounded bg-white shadow-[0_0_10px_rgba(0,0,0,0.1)] mt-[60px] overflow-auto">
          <p className="mt-[24px] mb-[20px] text-center text-[#1D2739] text-[24px] font-semibold">
            {isEditableSupplier ? "Edit supplier" : "Create a new supplier"}
          </p>

          {/* Tabs */}
          {!isEditableSupplier && (
            <>
              <div className="flex gap-6 mb-6 w-full justify-center">
                <button
                  onClick={() => setActiveTab("upload")}
                  className={`pb-2 text-[16px] font-medium ${
                    activeTab === "upload"
                      ? "text-[#1D2739] border-b-2 border-[#08F]"
                      : "text-[#BABABA]"
                  }`}
                >
                  Upload Supplier CSV file
                </button>
                <button
                  onClick={() => setActiveTab("manual")}
                  className={`pb-2 text-[16px] font-medium ${
                    activeTab === "manual"
                      ? "text-[#1D2739] border-b-2 border-[#08F]"
                      : "text-[#BABABA]"
                  }`}
                >
                  Create manually
                </button>
              </div>

              {/* Upload Tab */}
              {activeTab === "upload" && (
                <div className="w-full px-10">
                  <DragAndDropComponent
                    onFileChange={handleFileChange}
                    onDelete={handleDelete}
                    importedFiles={importedFiles}
                    setImportedFiles={setImportedFiles}
                    setPdfFile={setPdfFile}
                    setAutoFilledData={setAutoFilledData}
                    subtitle="CSV"
                  />
                  {importedFiles.length !== 0 && (
                    <div className="mt-10 w-full">
                      <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                        Add to folder
                      </label>
                      <Select
                        options={folderOptions}
                        value={
                          newSupplierData.folder?.name
                            ? folderOptions.find(
                                (opt) =>
                                  opt.value === newSupplierData.folder.name
                              )
                            : null
                        }
                        onChange={(selected) =>
                          setNewSupplierData((prev) => ({
                            ...prev,
                            folder: {
                              ...prev.folder,
                              name: selected.value.name,
                              numberOfSuppliers:
                                (prev.folder.numberOfSuppliers || 0) + 1,
                            },
                          }))
                        }
                        placeholder="Select Tier"
                        className="text-base"
                        styles={customStyles}
                        classNames={{
                          control: () =>
                            "border rounded-md shadow-sm min-h-[42px]",
                          valueContainer: () => "px-2",
                          menu: () => "z-[2000]",
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Manual Tab */}
          {(isEditableSupplier || activeTab === "manual") && (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col justify-center w-full sm:w-[572px] gap-y-4 px-10"
            >
              {currentStep === 1 && (
                <>
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Website
                    </label>
                    <input
                      type="text"
                      placeholder="website"
                      required
                      className="custom-edit-profile-input"
                      value={newSupplierData?.company?.website || ""}
                      onChange={(e) => handleChange("website", e.target.value)}
                    />
                  </div>

                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left items-center">
                      Company Name
                      <span className="text-red-500 ml-1">*</span>
                      <span>
                        <svg
                          onClick={() => setShowMessage(!showMessage)}
                          className="ml-1 relative"
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="14"
                          viewBox="0 0 14 15"
                          fill="none"
                        >
                          <path
                            d="M6.96732 11.5007C7.20065 11.5007 7.39798 11.42 7.55932 11.2587C7.72065 11.0973 7.8011 10.9002 7.80065 10.6673C7.80065 10.434 7.72021 10.2367 7.55932 10.0753C7.39843 9.91398 7.2011 9.83354 6.96732 9.83398C6.73398 9.83398 6.53687 9.91465 6.37598 10.076C6.2151 10.2373 6.13443 10.4344 6.13398 10.6673C6.13398 10.9007 6.21465 11.098 6.37598 11.2593C6.53732 11.4207 6.73443 11.5011 6.96732 11.5007ZM6.36732 8.93398H7.60065C7.60065 8.56732 7.64243 8.27843 7.72598 8.06732C7.80954 7.85621 8.04554 7.56732 8.43398 7.20065C8.72287 6.91176 8.95065 6.63665 9.11732 6.37532C9.28398 6.11398 9.36732 5.80021 9.36732 5.43398C9.36732 4.81176 9.13954 4.33398 8.68398 4.00065C8.22843 3.66732 7.68954 3.50065 7.06732 3.50065C6.43398 3.50065 5.92021 3.66732 5.52598 4.00065C5.13176 4.33398 4.85665 4.73398 4.70065 5.20065L5.80065 5.63398C5.85621 5.43398 5.98132 5.21732 6.17598 4.98398C6.37065 4.75065 6.66776 4.63398 7.06732 4.63398C7.42287 4.63398 7.68954 4.73132 7.86732 4.92598C8.0451 5.12065 8.13398 5.33443 8.13398 5.56732C8.13398 5.78954 8.06732 5.99798 7.93398 6.19265C7.80065 6.38732 7.63398 6.56776 7.43398 6.73398C6.9451 7.16732 6.6451 7.4951 6.53398 7.71732C6.42287 7.93954 6.36732 8.3451 6.36732 8.93398ZM7.00065 14.1673C6.07843 14.1673 5.21176 13.9924 4.40065 13.6427C3.58954 13.2929 2.88398 12.8178 2.28398 12.2173C1.68398 11.6173 1.2091 10.9118 0.859318 10.1007C0.50954 9.28954 0.334429 8.42287 0.333984 7.50065C0.333984 6.57843 0.509096 5.71176 0.859318 4.90065C1.20954 4.08954 1.68443 3.38398 2.28398 2.78398C2.88398 2.18398 3.58954 1.7091 4.40065 1.35932C5.21176 1.00954 6.07843 0.834429 7.00065 0.833984C7.92287 0.833984 8.78954 1.0091 9.60065 1.35932C10.4118 1.70954 11.1173 2.18443 11.7173 2.78398C12.3173 3.38398 12.7924 4.08954 13.1427 4.90065C13.4929 5.71176 13.6678 6.57843 13.6673 7.50065C13.6673 8.42287 13.4922 9.28954 13.142 10.1007C12.7918 10.9118 12.3169 11.6173 11.7173 12.2173C11.1173 12.8173 10.4118 13.2924 9.60065 13.6427C8.78954 13.9929 7.92287 14.1678 7.00065 14.1673ZM7.00065 12.834C8.48954 12.834 9.75065 12.3173 10.784 11.284C11.8173 10.2507 12.334 8.98954 12.334 7.50065C12.334 6.01176 11.8173 4.75065 10.784 3.71732C9.75065 2.68398 8.48954 2.16732 7.00065 2.16732C5.51176 2.16732 4.25065 2.68398 3.21732 3.71732C2.18398 4.75065 1.66732 6.01176 1.66732 7.50065C1.66732 8.98954 2.18398 10.2507 3.21732 11.284C4.25065 12.3173 5.51176 12.834 7.00065 12.834Z"
                            fill="#7E7E7E"
                          />
                        </svg>
                        {showMessage && (
                          <div className="flex leading-[10px] w-[146.087px] h-12 p-2 text-white text-[8px] font-normal rounded-[11.478px] bg-[rgba(33,33,33,0.68)] backdrop-blur-[2px] z-[1] absolute">
                            If you’re representing a company, providing the
                            company name is preferred.
                          </div>
                        )}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter company name"
                      required
                      className="custom-edit-profile-input w-full"
                      value={newSupplierData?.company?.name || ""}
                      onChange={(e) => handleChange("name", e.target.value)}
                    />
                  </div>

                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Email
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your company email"
                      required
                      className="custom-edit-profile-input w-full"
                      value={newSupplierData?.contact?.type[0]?.email}
                      onChange={(e) =>
                        setNewSupplierData((prevState) => ({
                          ...prevState,
                          contact: {
                            ...prevState.contact,
                            type: [
                              {
                                ...prevState.contact.type[0],
                                email: e.target.value,
                              },
                              ...prevState.contact.type.slice(1),
                            ],
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="w-full">
                    <label className="block text-base text-[#1d2739] mb-1 text-left">
                      Industry
                    </label>
                    <Select
                      options={industries.map(({ code, name }) => ({
                        value: code,
                        label: name,
                      }))}
                      value={
                        // newSupplierData.industry?.code &&
                        newSupplierData.industry?.name
                          ? {
                              value: newSupplierData.industry.name,
                              label: newSupplierData.industry.name,
                            }
                          : null
                      }
                      onChange={(selected) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          industry: selected
                            ? { name: selected.label, code: selected.value }
                            : null,
                        }));
                        setIndustrySelected(true);
                      }}
                      styles={customStyles}
                      placeholder="Select an Industry..."
                      isClearable
                      isSearchable
                    />
                  </div>
                  {(isIndustrySelected || newSupplierData.industry.name) && (
                    <div className="single-input w-full">
                      <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left items-center">
                        NAICS Codes
                        <span className="text-red-500 ml-1">*</span>
                      </label>

                      <Select
                        isMulti
                        placeholder="Select NAICS Codes..."
                        options={naicsData}
                        styles={customStyles}
                        value={
                          newSupplierData.identifiers?.identifierDetails
                            ?.filter((item) => item.name === "NAICS")
                            .map((item) => ({
                              value: item.code,
                              label: item.code,
                            })) || []
                        }
                        onChange={(selected) => {
                          const naicsArray = selected.map((s) => ({
                            name: "NAICS",
                            description: s.label,
                            code: s.value,
                            type: "NAICS",
                          }));

                          setNewSupplierData((prev) => ({
                            ...prev,
                            identifiers: {
                              ...prev.identifiers,
                              identifierDetails: [
                                ...(
                                  prev.identifiers?.identifierDetails || []
                                ).filter((item) => item.type !== "NAICS"),
                                ...naicsArray,
                              ],
                            },
                          }));
                          setNaicsSelected(true);
                        }}
                      />
                    </div>
                  )}
                  <div className="w-full mb-2">
                    <label className="block text-base text-[#1d2739] mb-1 text-left">
                      Company Size
                    </label>
                    <Select
                      options={sizeOptions}
                      value={
                        sizeOptions.find(
                          (opt) =>
                            opt.value ===
                            `${newSupplierData?.company?.businessSize?.min}-${newSupplierData?.company?.businessSize?.max}`
                        ) || sizeOptions[0]
                      }
                      onChange={(selected) => {
                        const [min, max] = selected.value.split("-");
                        setNewSupplierData((prev) => ({
                          ...prev,
                          company: {
                            ...prev.company,
                            businessSize: {
                              ...prev.company.businessSize,
                              min: min || "",
                              max: max || "",
                            },
                          },
                        }));
                      }}
                      styles={customStyles}
                    />
                  </div>
                </>
              )}

              {currentStep === 2 && (
                <>
                  {/* Country */}
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Country
                    </label>
                    <Select
                      options={countryOptions}
                      value={
                        countryOptions.find(
                          (opt) =>
                            opt.value ===
                            newSupplierData?.address?.type[0]?.country
                        ) || countryOptions[0]
                      }
                      onChange={(selected) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            type: [
                              {
                                ...prev.address.type[0],
                                country: selected.value,
                              },
                            ],
                          },
                        }));
                      }}
                      styles={customStyles}
                      isOptionDisabled={(option) => option.isDisabled}
                    />
                  </div>

                  {/* State or Province */}
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      {newSupplierData?.address?.type[0]?.country === "USA" ||
                      newSupplierData?.address?.type[0]?.country ===
                        "United States"
                        ? "State"
                        : "Province"}
                    </label>
                    <Select
                      options={stateOptions}
                      value={
                        stateOptions.find(
                          (opt) =>
                            opt.value ===
                              newSupplierData?.address?.type[0]?.state ||
                            opt.label ===
                              stateAbbreviations[
                                newSupplierData?.address?.type[0]?.state
                              ]
                        ) || stateOptions[0]
                      }
                      onChange={(selected) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            type: [
                              {
                                ...prev.address.type[0],
                                state: selected.value,
                              },
                            ],
                          },
                        }));
                      }}
                      styles={customStyles}
                      isOptionDisabled={(option) => option.isDisabled}
                    />
                  </div>

                  {/* City */}
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      City
                    </label>
                    <input
                      type="text"
                      placeholder="City"
                      required
                      className="custom-edit-profile-input"
                      value={newSupplierData?.address?.type[0]?.city || ""}
                      onChange={(e) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            type: [
                              {
                                ...prev.address.type[0],
                                city: e.target.value,
                              },
                            ],
                          },
                        }));
                      }}
                    />
                  </div>

                  {/* Street */}
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Street
                    </label>
                    <input
                      type="text"
                      placeholder="Street"
                      required
                      className="custom-edit-profile-input"
                      value={newSupplierData?.address?.type[0]?.street}
                      onChange={(e) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            type: [
                              {
                                ...prev.address.type[0],
                                street: e.target.value,
                              },
                            ],
                          },
                        }));
                      }}
                    />
                  </div>

                  {/* ZIP / Postal Code */}
                  <div className="flex flex-col gap-4">
                    <div>
                      <label
                        htmlFor="postal_code"
                        className="block text-[#1D2639] text-base text-start font-normal"
                      >
                        {newSupplierData?.address?.type[0]?.country === "USA" ||
                        newSupplierData?.address?.type[0]?.country ===
                          "United States"
                          ? "ZIP Code"
                          : "Postal Code"}{" "}
                      </label>
                      <input
                        type="text"
                        id="postal_code"
                        name="postal_code"
                        required
                        placeholder={
                          newSupplierData?.address?.type[0]?.country ===
                            "USA" ||
                          newSupplierData?.address?.type[0]?.country ===
                            "United States"
                            ? "Enter ZIP Code (e.g., 12345)"
                            : "Enter Postal Code (e.g., K1A 0B1)"
                        }
                        className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
                        value={
                          newSupplierData?.address?.type[0]?.postalCode || ""
                        }
                        onChange={(e) => {
                          setNewSupplierData((prev) => ({
                            ...prev,
                            address: {
                              ...prev.address,
                              type: [
                                {
                                  ...prev.address.type[0],
                                  postalCode: e.target.value,
                                  zip_01: e.target.value,
                                },
                              ],
                            },
                          }));
                        }}
                      />
                      {zipError && (
                        <p className="text-red-500 text-sm text-start">
                          {zipError}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {currentStep === 3 && (
                <>
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Primary Contact
                    </label>
                    <input
                      type="text"
                      placeholder="Primary contact"
                      required
                      className="custom-edit-profile-input"
                      value={`${
                        newSupplierData?.contact?.type[0]?.name?.firstName || ""
                      } ${
                        newSupplierData?.contact?.type[0]?.name?.lastName || ""
                      }`.trim()}
                      onChange={(e) => {
                        const input = e.target.value;
                        setFullName(input);

                        const [firstName, ...rest] = input.split(" ");
                        const lastName = rest.join(" ");

                        setNewSupplierData((prevState) => ({
                          ...prevState,
                          contact: {
                            ...prevState.contact,
                            type: [
                              {
                                ...prevState.contact.type[0],
                                name: {
                                  ...prevState.contact.type[0].name,
                                  firstName,
                                  lastName,
                                },
                              },
                            ],
                          },
                        }));
                      }}
                    />
                  </div>
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      DUNS Number
                    </label>
                    <input
                      type="text"
                      placeholder="DUNS Number"
                      required
                      className="custom-edit-profile-input"
                      value={
                        newSupplierData.identifiers?.identifierDetails?.find(
                          (item) => item.name === "DUNS" || item.type === "DUNS"
                        )?.code || ""
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;

                        setNewSupplierData((prev) => {
                          const existing =
                            prev.identifiers?.identifierDetails || [];

                          const hasDuns = existing.some(
                            (item) =>
                              item.name === "DUNS" || item.type === "DUNS"
                          );

                          if (hasDuns) {
                            return {
                              ...prev,
                              identifiers: {
                                ...prev.identifiers,
                                identifierDetails: existing.map((item) =>
                                  item.name === "DUNS" || item.type === "DUNS"
                                    ? { ...item, code: newValue }
                                    : item
                                ),
                              },
                            };
                          }

                          return {
                            ...prev,
                            identifiers: {
                              ...prev.identifiers,
                              identifierDetails: [
                                ...existing,
                                {
                                  name: "",
                                  description: "",
                                  code: newValue,
                                  type: "DUNS",
                                },
                              ],
                            },
                          };
                        });
                      }}
                    />
                  </div>
                  <div className="single-input w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      EIN
                    </label>
                    <input
                      type="text"
                      placeholder="EIN Number"
                      required
                      className="custom-edit-profile-input"
                      value={
                        newSupplierData.identifiers?.identifierDetails?.find(
                          (item) => item.name === "EIN" || item.type === "EIN"
                        )?.code || ""
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;

                        setNewSupplierData((prev) => {
                          const existing =
                            prev.identifiers?.identifierDetails || [];

                          const hasDuns = existing.some(
                            (item) => item.type === "EIN" || item.name === "EIN"
                          );

                          if (hasDuns) {
                            return {
                              ...prev,
                              identifiers: {
                                ...prev.identifiers,
                                identifierDetails: existing.map((item) =>
                                  item.type === "EIN" || item.name === "EIN"
                                    ? { ...item, code: newValue }
                                    : item
                                ),
                              },
                            };
                          }

                          return {
                            ...prev,
                            identifiers: {
                              ...prev.identifiers,
                              identifierDetails: [
                                ...existing,
                                {
                                  name: "",
                                  description: "",
                                  code: newValue,
                                  type: "EIN",
                                },
                              ],
                            },
                          };
                        });
                      }}
                    />
                  </div>
                  <div className="w-full">
                    <label className="flex text-[#1D2739] text-base font-normal mb-[7px] text-left">
                      Add to folder
                    </label>

                    <Select
                      options={folderOptions}
                      value={selectedFolderOption}
                      onChange={(selected) => {
                        setNewSupplierData((prev) => ({
                          ...prev,
                          folder: {
                            ...prev.folder,
                            name: selected.value.name,
                          },
                        }));
                      }}
                      placeholder="Select Folder"
                      styles={customStyles}
                      className="text-base"
                      classNames={{
                        control: () =>
                          "border rounded-md shadow-sm min-h-[42px]",
                        valueContainer: () => "px-2",
                        menu: () => "z-[2000]",
                      }}
                    />
                  </div>
                </>
              )}
            </form>
          )}
        </div>

        <div
          className={`flex flex-col justify-center items-center absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
            activeTab === "upload" ? "bottom-12" : "bottom-0"
          } gap-2`}
        >
          <button
            className={`flex w-[432px] h-[56px] p-5 justify-center items-center gap-2 rounded-[13px] ${
              formValid
                ? "bg-[#EAF524] cursor-pointer"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            onClick={() => {
              if (!formValid) return;
              if (currentStep === 1 && importedFiles.length > 0) {
                handleCreateByFile();
              } else if (currentStep === 3 && isEditableSupplier) {
                handleEditConfirmation();
              } else {
                handleSubmit();
              }
            }}
            disabled={!formValid}
          >
            {currentStep === 1
              ? "Proceed"
              : currentStep === 3 && isEditableSupplier
              ? "Save"
              : currentStep !== 1 && currentStep !== 3
              ? "Continue"
              : "Create"}
          </button>
          <span className="text-[#7E7E7E]" onClick={() => handleCancel()}>
            Cancel
          </span>
        </div>
      </div>

      {isProceed && <MappingTable suppliers={autoFilledData} />}
    </div>
  );
};

export default CreateNewSupplierForm;
